// Till / Add / kid choice screen (R-F, Aug 2026) — the real-world spend logger and the
// gift/pocket-money Add flow, per TILL_SCOPE.md. Net-new surface (register: net-new); the
// money maths is deliberately NOT new: affordability and the tokens-first split reuse the
// exact helpers goal redemption uses (goalProgress / resolveRedeemSplit), and £→token
// conversion is penceToTokens — already round-to-NEAREST, which is the recorded rounding
// decision. A split spend writes TWO linked transactions (shared tillId) — also a recorded
// decision — via the untouched shared addTransaction. The kid's "are you sure" screen is
// parent-triggered and IN PERSON (hand the phone over): it renders on this device, full
// screen, with equal-weight Buy/Save options per the icon set's choice-screen neutrality
// rule; choosing to save gets the celebration.
import { Celebration, Icon } from "../components/Common.jsx";
import { CURRENCY_SYMBOL, uid } from "../lib/constants.js";
import { fmtPence, goalProgress, goalTargetTokens, penceToTokens, pickTopGoal, resolveRedeemSplit, tokensToPence } from "../lib/utils.js";
import { t as ct } from "../lib/content.js";
import { useBackLayer } from "../lib/backstack.js";
import { ageBand, track } from "../lib/metrics.js";
const { useEffect, useMemo, useState } = React;

// R-H (Aug 2026): impact anchors on the SAME goal Home shows — pickTopGoal (kid-starred
// first, else furthest along; reuse-verbatim). The first Till build hand-rolled a
// highest-% pick here, duplicating the helper and able to disagree with the kid's own
// starred goal; TILL_SCOPE.md corrected in the same round.

/* --------------------------------- Till ---------------------------------- */
// £-first spend logger. Lives inside a KidDetail focus window; onComplete closes it
// (origin-aware close returns a Home-initiated parent straight to Home — R-E).
export function TillSheet({ child, state, addTransaction, onComplete, update }) {
  // W2.5 Till funnel top: opened → logged. One event per sheet mount.
  useEffect(() => {
    track("till_opened", { band: ageBand(child) || undefined });
  }, []);
  const rate = state.settings?.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const [what, setWhat] = useState("");
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");
  const [choiceOpen, setChoiceOpen] = useState(false);
  useBackLayer(choiceOpen, () => setChoiceOpen(false)); // LB-315: back is the ✕ back to the Till

  const pricePence = useMemo(() => {
    const raw = parseFloat(price);
    return raw > 0 ? Math.round(raw * 100) : 0;
  }, [price]);
  const tokensNeeded = pricePence > 0 ? penceToTokens(pricePence, rate) : 0;
  const availableTokens = child.tokenBalance + penceToTokens(child.vaultBalancePence, rate);
  const affordable = tokensNeeded > 0 && tokensNeeded <= availableTokens;
  // Tokens-first split — the same resolver goal redemption uses (no chosen source: Till
  // has no source toggle in v1, per scope).
  const split = affordable ? resolveRedeemSplit(child, tokensNeeded, rate) : { tokenPart: 0, vaultPart: 0 };
  const vaultPence = split.vaultPart > 0 ? tokensToPence(split.vaultPart, rate) : 0;
  const impact = useMemo(() => {
    const goal = pickTopGoal(child.goals, child, rate);
    if (!goal || !affordable) return null;
    const before = goalProgress(child, goalTargetTokens(goal, rate), rate).pct;
    const after = goalProgress(
      { ...child, tokenBalance: child.tokenBalance - split.tokenPart, vaultBalancePence: (child.vaultBalancePence || 0) - vaultPence },
      goalTargetTokens(goal, rate),
      rate
    ).pct;
    return { title: goal.title, before, after };
  }, [child, rate, affordable, split.tokenPart, vaultPence]);

  function validate() {
    if (!what.trim()) { setError(ct("till.error_reason", "What\u2019s the purchase?")); return false; }
    if (!(pricePence > 0)) { setError(ct("till.error_price", "Enter a price greater than zero.")); return false; }
    if (!affordable) { setError(ct("till.error_unaffordable", "{name} can't cover that yet.", { name: child.name })); return false; }
    setError("");
    return true;
  }

  // The recorded two-linked-transactions decision: one token spend + one vault spend
  // sharing a tillId, both tagged till at write time (convention: tag, don't new-type).
  function logSpend() {
    const tillId = uid();
    const reason = what.trim();
    if (split.tokenPart > 0) addTransaction(child.id, split.tokenPart, reason, "spend", "token", undefined, { till: true, tillId });
    if (split.vaultPart > 0) addTransaction(child.id, vaultPence, reason, "spend", "vault", undefined, { till: true, tillId });
    // W2.5: till_logged closes the opened→logged funnel; till_spend is the feature-adoption
    // event with the £ value (pence) — one spend even when the split wrote two rows.
    track("till_logged", { band: ageBand(child) || undefined });
    track("till_spend", { band: ageBand(child) || undefined, value: pricePence });
    if (onComplete) onComplete();
  }
  function submitLog() { if (validate()) logSpend(); }
  function submitAsk() { if (validate()) setChoiceOpen(true); }

  const pctUsedByTokens = tokensNeeded > 0 ? Math.round((split.tokenPart / tokensNeeded) * 100) : 0;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-base font-semibold text-slate-500 w-24 flex-shrink-0">{ct("till.what_label", "What for")}</label>
        <input className="border border-slate-200 rounded-lg px-3 py-2 text-base flex-1 min-w-0" placeholder={ct("till.what_placeholder", "e.g. Ice cream, comic, arcade")} value={what} onChange={(e) => setWhat(e.target.value)} />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <label className="text-base font-semibold text-slate-500 w-24 flex-shrink-0">{ct("till.price_label", "Price")}</label>
        <input className="border border-slate-200 rounded-lg px-3 py-2 text-base flex-1 min-w-0" placeholder={`${CURRENCY_SYMBOL} amount`} type="number" min="0.01" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
      </div>

      {pricePence > 0 && (
        <div className="bg-slate-50 rounded-xl p-3 mb-3" data-testid="till-summary">
          <div className="text-sm font-semibold text-slate-700 mb-1.5">{ct("till.tokens_line", "= {tokens} tokens", { tokens: tokensNeeded })}</div>
          {affordable ? (
            <>
              {/* Affordability bar — gold token part, teal vault top-up, matching the kid
                  goal bar's two-tone convention so the same money reads the same way. */}
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden flex mb-1.5" aria-hidden="true">
                <div className="h-full bg-amber-400" style={{ width: `${pctUsedByTokens}%` }} />
                <div className="h-full bg-teal-500" style={{ width: `${100 - pctUsedByTokens}%` }} />
              </div>
              <div className="text-xs text-slate-500">
                {split.vaultPart > 0
                  ? ct("till.split_line", "{tokens} tokens + {vault} from the Savings Vault", { tokens: split.tokenPart, vault: fmtPence(vaultPence) })
                  : ct("till.tokens_only_line", "{tokens} tokens, no Savings Vault needed", { tokens: split.tokenPart })}
              </div>
              {/* R-H: covered goal → plain reassurance; a real drop → the arrow; no visible
                  change → no line ("100% → 100%" is noise — Alan, device pass). */}
              {impact && impact.after >= 100 && (
                <div className="text-xs text-slate-500 mt-1">{ct("till.impact_covered", "\u201C{title}\u201D stays fully saved after this", { title: impact.title })}</div>
              )}
              {impact && impact.after < 100 && impact.before > impact.after && (
                <div className="text-xs text-slate-500 mt-1">{ct("till.impact_line", "\u201C{title}\u201D goes {before}% → {after}% saved", { title: impact.title, before: impact.before, after: impact.after })}</div>
              )}
            </>
          ) : (
            <div className="text-xs font-semibold text-rose-600">{ct("till.unaffordable_line", "{name} has {have} tokens available (tokens + Savings Vault) — {short} short.", { name: child.name, have: availableTokens, short: tokensNeeded - availableTokens })}</div>
          )}
        </div>
      )}

      {error && <div className="text-rose-600 text-base font-semibold mb-2">{error}</div>}
      <div className="flex flex-col gap-2">
        <button className="w-full font-bold text-sm rounded-lg py-2.5 text-white bg-slate-900 disabled:opacity-40" disabled={pricePence > 0 && !affordable} onClick={submitLog}>{ct("till.log_button", "Log spend")}</button>
        <button className="w-full font-bold text-sm rounded-lg py-2.5 bg-pill text-navy disabled:opacity-40" disabled={pricePence > 0 && !affordable} onClick={submitAsk}>{ct("till.ask_button", "Ask {name} first", { name: child.name })}</button>
      </div>

      {choiceOpen && (
        <ChoiceScreen
          child={child}
          what={what.trim()}
          pricePence={pricePence}
          tokensNeeded={tokensNeeded}
          impact={impact}
          rate={rate}
          mode="till"
          onBuy={() => { setChoiceOpen(false); logSpend(); }}
          // LB-17: the child turns the thing they wanted into a goal from the Till's own
          // choice screen too — same shape as the ask flow, via the shared goal shape
          // (targetPence, LB-355) so the price is kept exactly as typed.
          onGoal={update ? () => update((s) => { const c = s.profiles.find((p) => p.id === child.id); if (!c) return null; c.goals = [...(c.goals || []), { id: uid(), title: what.trim(), targetPence: pricePence, image: null }]; return s; }) : undefined}
          onSave={() => { setChoiceOpen(false); if (onComplete) onComplete(); }}
          onBack={() => setChoiceOpen(false)}
        />
      )}
    </div>
  );
}

/* ----------------------------- Choice screen ------------------------------ */
// Kid-facing. Two callers since LB-17 (28 Sep 2026):
//   mode "till" — the parent hands this device over IN PERSON (recorded decision); the first
//                 option logs the spend now.
//   mode "ask"  — the child got here from their own Rewards tab; the first option SENDS the
//                 ask to a grown-up and nothing moves until they say yes.
// Redrawn as a flat kid-mode sheet (Alan, 28 Sep: the tiles read "babyish" and off the app's
// pattern): hairline header with the close, rows for the money, the impact as a tinted panel,
// then THREE options of identical weight — spend/ask, Make it a goal, Keep saving — per the
// icon set's choice-screen neutrality rule; only the tint differs (gold = spending, teal =
// goals, navy = saving). Make it a goal is the child's own act (Alan, 28 Sep — "let the kid
// turn it into a goal, not just the parent"): the goal lands in My goals at this price, nothing
// is sent or spent, and the saving celebration plays. Keep saving celebrates as it always did.
// Portaled above whatever opened it (z-50).
export function ChoiceScreen({ child, what, pricePence, tokensNeeded, impact, availability, rate, mode = "till", onBuy, onGoal, onSave, onBack }) {
  const [done, setDone] = useState(null); // null | "saved" | "goal"
  const avail = availability || { availableTokens: child.tokenBalance + penceToTokens(child.vaultBalancePence, rate), shortfall: 0, affordable: true };
  const short = avail.shortfall > 0;
  const pctThere = tokensNeeded > 0 ? Math.min(100, Math.round((avail.availableTokens / tokensNeeded) * 100)) : 0;
  const primaryLabel = mode === "ask"
    ? (short ? ct("choice.ask_anyway_option", "Ask anyway") : ct("choice.ask_option", "Ask a grown-up"))
    : ct("choice.buy_option", "Buy it now");
  const rows = [
    [what, fmtPence(pricePence), "text-slate-900 font-extrabold"],
    [ct("choice.in_tokens_label", "In tokens"), ct("choice.in_tokens_value", "{tokens} tokens", { tokens: tokensNeeded }), "text-amber-600 font-bold"],
    [ct("choice.you_have_label", "You have"), ct("choice.you_have_value", "{tokens} tokens + {vault} vault", { tokens: child.tokenBalance, vault: fmtPence(child.vaultBalancePence) }), "text-slate-900 font-bold"],
    [ct("choice.after_label", "After buying"), short ? ct("choice.after_short", "{tokens} tokens short", { tokens: avail.shortfall }) : ct("choice.after_left", "{tokens} tokens left", { tokens: avail.availableTokens - tokensNeeded }), short ? "text-rose-600 font-bold" : "text-slate-900 font-bold"],
  ];
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 bg-white flex flex-col" role="dialog" aria-label="Are you sure?" data-testid="choice-screen" data-mode={mode}>
      <div className="px-5 pb-3.5 flex items-center justify-between border-b border-slate-200 w-full max-w-lg mx-auto" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 2.75rem)" }}>
        <div className="text-xl font-extrabold text-slate-900">
          {done === "saved" ? ct("choice.saved_title", "Brilliant saving, {name}", { name: child.name })
            : done === "goal" ? ct("choice.goal_title", "It\u2019s a goal now, {name}", { name: child.name })
            : ct("choice.title", "{name}, are you sure?", { name: child.name })}
        </div>
        {!done && (
          <button onClick={onBack} aria-label="Close" className="w-9 h-9 rounded-lg bg-pill text-navy flex items-center justify-center flex-shrink-0"><Icon id="ic-close" size={16} style={{ color: "currentColor" }} /></button>
        )}
      </div>

      <div className="flex-1 px-5 pt-2 pb-6 flex flex-col gap-5 overflow-y-auto w-full max-w-lg mx-auto" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}>
        {done ? (
          <div className="flex flex-col items-center text-center pt-6">
            <Celebration variant="coins" />
            <p className="text-base text-slate-500 mb-8 mt-2">
              {done === "goal"
                ? ct("choice.goal_body", "\u201C{title}\u201D is in My goals at {price}. Keep saving.", { title: what, price: fmtPence(pricePence) })
                : ct("choice.saved_body", "Your money stays put, and \u201C{goal}\u201D just got closer.", { goal: impact ? impact.title : ct("choice.saved_body_no_goal_fallback", "your next goal") })}
            </p>
            <button className="bg-slate-900 text-white font-bold rounded-lg px-10 py-3.5 text-base" onClick={onSave}>{ct("choice.saved_done", "Done")}</button>
          </div>
        ) : (
          <>
            <div className="flex flex-col" data-testid="choice-rows">
              {rows.map(([label, value, cls], i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-3 border-b border-slate-200 text-base">
                  <span className={`min-w-0 break-words ${i === 0 ? "text-slate-900 font-bold" : "text-slate-500 font-semibold"}`}>{label}</span>
                  <span className={`flex-shrink-0 ${cls}`}>{value}</span>
                </div>
              ))}
            </div>
            {/* The impact line (LB-17): a real drop → the Till's own arrow line; a covered goal
                → plain reassurance; a shortfall → how far along they are, and that a goal is
                the way to get there. No line when nothing visibly changes. */}
            {short ? (
              <div className="bg-pill text-navy text-sm font-semibold rounded-lg px-3.5 py-3" data-testid="choice-impact">{ct("choice.short_line", "You can\u2019t cover this yet. You\u2019re {pct}% of the way there.", { pct: pctThere })}</div>
            ) : impact && impact.after >= 100 ? (
              <div className="bg-pill text-navy text-sm font-semibold rounded-lg px-3.5 py-3" data-testid="choice-impact">{ct("till.impact_covered", "\u201C{title}\u201D stays fully saved after this", { title: impact.title })}</div>
            ) : impact && impact.after < 100 && impact.before > impact.after ? (
              <div className="bg-pill text-navy text-sm font-semibold rounded-lg px-3.5 py-3" data-testid="choice-impact">{ct("till.impact_line", "\u201C{title}\u201D goes {before}% \u2192 {after}% saved", { title: impact.title, before: impact.before, after: impact.after })}</div>
            ) : null}
            <div className="flex-1" />
            {/* Equal weight, per the icon set's choice-screen neutrality rule: identical size,
                identical typography — only the palette-true tints differ. */}
            <div className="flex flex-col gap-2.5">
              <button className="w-full min-h-[52px] rounded-lg bg-amber-50 text-slate-900 text-base font-extrabold flex items-center justify-center gap-2 active:bg-amber-100" onClick={onBuy} data-testid="choice-primary">
                <Icon id={mode === "ask" ? "ic-wish" : "ic-buy"} size={20} hue="gold" />{primaryLabel}
              </button>
              <button className="w-full min-h-[52px] rounded-lg bg-teal-50 text-slate-900 text-base font-extrabold flex items-center justify-center gap-2 active:bg-teal-100" onClick={() => { if (onGoal) onGoal(); setDone("goal"); }} data-testid="choice-goal">
                <Icon id="ic-goals" size={20} hue="teal" />{ct("choice.goal_option", "Make it a goal")}
              </button>
              <button className="w-full min-h-[52px] rounded-lg bg-pill text-slate-900 text-base font-extrabold flex items-center justify-center gap-2" onClick={() => setDone("saved")} data-testid="choice-save">
                <Icon id="ic-supersaver" size={20} hue="slate" />{ct("choice.save_option", "Keep saving")}
              </button>
            </div>
            <div className="text-sm text-slate-500 text-center">
              {mode === "ask"
                ? ct("choice.foot_ask", "Ask: a grown-up decides and nothing moves until they say yes. Goal: it goes in My goals at {price}.", { price: fmtPence(pricePence) })
                : ct("choice.foot_till", "Buying logs the spend now. Goal: it goes in My goals at {price}.", { price: fmtPence(pricePence) })}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ---------------------------------- Add ----------------------------------- */
// Gift/pocket-money framing — the recorded requirement that Add must take MONEY (£ straight
// into the Savings Vault) as well as tokens. Credits only; corrections stay in Manage
// balances. Same shared addTransaction; vault amounts are pence.
export function AddSheet({ child, addTransaction, onComplete }) {
  const [kind, setKind] = useState("token"); // "token" | "vault"
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  function submit() {
    const raw = parseFloat(amount);
    if (!reason.trim()) { setError(ct("add.error_reason", "A reason is required, for example pocket money.")); return; }
    if (!raw || raw <= 0) { setError(kind === "vault" ? ct("add.error_amount_vault", "Enter a {symbol} amount greater than zero.", { symbol: CURRENCY_SYMBOL }) : ct("add.error_amount_token", "Enter a token amount greater than zero.")); return; }
    setError("");
    const amt = kind === "vault" ? Math.round(raw * 100) : Math.round(raw);
    addTransaction(child.id, amt, reason.trim(), "credit", kind, undefined, { addMoney: true });
    track("money_added", { kind, band: ageBand(child) || undefined, value: amt });
    if (onComplete) onComplete();
  }

  return (
    <div>
      <div className="flex rounded-xl overflow-hidden border border-slate-200 mb-3">
        <button className={`flex-1 py-2 text-sm font-bold ${kind === "token" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} onClick={() => setKind("token")}>{ct("add.tokens_option", "Tokens")}</button>
        <button className={`flex-1 py-2 text-sm font-bold ${kind === "vault" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} onClick={() => setKind("vault")}>{ct("add.vault_option", "Money to Savings Vault")}</button>
      </div>
      <p className="text-base text-slate-400 mb-2">{kind === "vault" ? ct("add.vault_hint", "Pocket money or gift money — goes straight into {name}'s Savings Vault.", { name: child.name }) : ct("add.tokens_hint", "A one-off token top-up for {name}.", { name: child.name })}</p>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-base font-semibold text-slate-500 w-24 flex-shrink-0">{ct("add.reason_label", "Reason")}</label>
        <input className="border border-slate-200 rounded-lg px-3 py-2 text-base flex-1 min-w-0" placeholder={ct("add.reason_placeholder", "e.g. Pocket money, Birthday gift")} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-base font-semibold text-slate-500 w-24 flex-shrink-0">{ct("add.amount_label", "Amount")}</label>
        <input className="border border-slate-200 rounded-lg px-3 py-2 text-base flex-1 min-w-0" placeholder={kind === "vault" ? `${CURRENCY_SYMBOL} amount` : ct("add.amount_placeholder_tokens", "Token amount")} type="number" min="0.01" step={kind === "vault" ? "0.01" : "1"} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      {error && <div className="text-rose-600 text-base font-semibold mb-2">{error}</div>}
      <button className={`w-full font-bold text-sm rounded-lg py-2.5 text-white bg-slate-900`} onClick={submit}>{kind === "vault" ? ct("add.submit_vault", "Add to Savings Vault") : ct("add.submit_tokens", "Add tokens")}</button>
    </div>
  );
}
